import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { startPortalLogin } from '@/auth/api';
import { branding } from './branding';
import { LoadingState } from '@/shared/components';

/**
 * Chrome for the administrative side of the app.
 *
 * Public form pages deliberately do not use it: a respondent is not a user of
 * TacoBots and should never see navigation into it.
 */
export function AppShell() {
  const { user, config, isLoading, signOut } = useAuth();
  const location = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const here = location.pathname + location.search;
  // Once TacoBots is the only way in there is nothing to choose between, so the
  // sign-in page would be a single button. Go straight to the portal instead;
  // if the shared cookie is already there the round trip is invisible.
  const portalOnly = Boolean(
    config && config.portalEnabled && !config.localEnabled && !config.entraEnabled,
  );
  const shouldUsePortal = !isLoading && !user && portalOnly;

  useEffect(() => {
    if (shouldUsePortal) startPortalLogin(here);
  }, [shouldUsePortal, here]);

  if (isLoading) return <LoadingState label="Verificando su sesión…" />;
  if (shouldUsePortal) return <LoadingState label="Llevándolo a TacoBots…" />;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: here }} />;
  }

  const initials = user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box
            component={Link}
            to="/dashboard"
            sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}
            aria-label={branding.appName}
          >
            <Box
              component="img"
              src={branding.logoUrl}
              alt={branding.appName}
              sx={{ height: 26 }}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          <Button component={Link} to="/dashboard" color="inherit" sx={{ textTransform: 'none' }}>
            Mis formularios
          </Button>

          <IconButton onClick={(event) => setAnchor(event.currentTarget)} aria-label="Cuenta">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
              {initials || '?'}
            </Avatar>
          </IconButton>

          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{user.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={async () => {
                setAnchor(null);
                await signOut();
              }}
            >
              <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
              Cerrar sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 3, sm: 4 } }}>
        <Outlet />
      </Container>

      <Box component="footer" sx={{ py: 3, borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ justifyContent: 'space-between' }}
          >
            <Typography variant="caption" color="text.secondary">
              {branding.appName} · {branding.organisationName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Horas en {branding.timezone.replace('_', ' ')}
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
